import * as React from "react";

import {
  AssociationType,
  fileAssociationType,
} from "@/shared/domain/association";
import { useDatasetsQuery } from "@/shared/domain/datasets/hooks";
import { useAllDevicesByOrg } from "@/shared/domain/devices/hooks/useAllDevicesByOrg";
import { useAllDeviceTypesByOrg } from "@/shared/domain/deviceTypes/hooks";
import type { SearchQueryBody } from "@/types";

/**
 * Human-readable names for what holds a handful of files, by association ID:
 * a dataset's name, or a device's or device type's name followed by its ID.
 *
 * A flat file list has no headings to sit under, so each row has to name what
 * holds it. IDs are always available on the file record and names are not,
 * which decides the contract: the returned function **always** answers, with
 * the name once it is known and the ID until then, or for good when there is
 * no name to look up (an org's own files).
 *
 * The ID is a true label while the name is unknown, and it still tells two
 * same-named files apart, which is what the label is for.
 *
 * Datasets are looked up by the IDs actually on screen rather than by listing
 * the org's datasets: a picker showing ten files should not pull a thousand
 * dataset records, and an org with more datasets than one page holds would
 * otherwise leave the overflow permanently unnamed. Devices and device types
 * cannot be looked up by their `dv_` and `dt_` IDs, so the org's whole list of
 * each is read instead, and only when a row on screen needs it.
 *
 * @param orgId the org the files belong to
 * @param associationIds the IDs needing a label; an empty list issues no request
 * @returns a stable lookup from association ID to its display label
 */
export function useAssociationLabels(
  orgId: string,
  associationIds: string[],
): (associationId: string) => string {
  // Sorted and de-duplicated so the query key depends on the *set* of IDs, not
  // the order the file listing returned them in, and a re-sort of the same
  // datasets refetches no names.
  const ids = React.useMemo(
    () => Array.from(new Set(associationIds)).sort(),
    [associationIds],
  );
  const datasetIds = React.useMemo(
    () =>
      ids.filter((id) => fileAssociationType(id) === AssociationType.Dataset),
    [ids],
  );
  const needsDevices = ids.some(
    (id) => fileAssociationType(id) === AssociationType.Device,
  );
  const needsDeviceTypes = ids.some(
    (id) => fileAssociationType(id) === AssociationType.DeviceType,
  );

  const query = React.useMemo<SearchQueryBody>(
    () => ({
      condition: {
        operator: "OR",
        conditions: datasetIds.map((id) => ({
          field: "dataset_id",
          comparator: "EQUALS" as const,
          value: id,
        })),
      },
    }),
    [datasetIds],
  );

  const datasetsQuery = useDatasetsQuery({
    // Withholding the org is how these queries stay disabled: an OR over zero
    // conditions is not a query the backend accepts, and asking for the names
    // of nothing has no answer worth waiting for.
    orgId: datasetIds.length === 0 ? undefined : orgId,
    query,
  });
  const devicesQuery = useAllDevicesByOrg(needsDevices ? orgId : undefined);
  const deviceTypesQuery = useAllDeviceTypesByOrg(
    needsDeviceTypes ? orgId : undefined,
  );

  const names = React.useMemo(() => {
    const byId = new Map<string, string>();
    for (const dataset of datasetsQuery.data ?? []) {
      // A dataset's name is nullable, and an all-whitespace one is no more
      // recognizable than none at all; both fall through to the ID.
      const name = dataset.name?.trim();
      if (name) {
        byId.set(dataset.dataset_id, name);
      }
    }
    for (const device of devicesQuery.data ?? []) {
      if (device.universal_device_id) {
        byId.set(
          device.universal_device_id,
          `${device.device_id} (${device.universal_device_id})`,
        );
      }
    }
    for (const deviceType of deviceTypesQuery.data ?? []) {
      byId.set(
        deviceType.device_type_id,
        `${deviceType.name} (${deviceType.device_type_id})`,
      );
    }
    return byId;
  }, [datasetsQuery.data, devicesQuery.data, deviceTypesQuery.data]);

  return React.useCallback(
    (associationId: string) => names.get(associationId) ?? associationId,
    [names],
  );
}
